import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as Sentry from "@sentry/nextjs";
import { Row, Col } from "react-bootstrap";

import { FORM_FIELD_KEYS } from "../../../../../constants/forms";
import { getFieldProps } from "../../../../../utils/form-fields";

import TextInput from "../../../controls/TextInput";
import Select from "../../../controls/Select";

function createOptions(rawItems = []) {
  return rawItems.map(({ code, label }) => {
    return {
      value: code,
      title: label,
    };
  });
}

function renderExtraFields(item = {}, values, onMouseEnter, onMouseLeave, onChange, start = 1, max = 4) {
  if (item.labelForDescriptionField) {
    return (
      <>
        <Col xs={12}>
          <div className="input-title">
            <span>{item.labelForDescriptionField}</span>
          </div>
        </Col>
        <Col xs={12}>
          <TextInput
            {...getFieldProps(FORM_FIELD_KEYS.productDescription)}
            placeholder={getFieldProps(FORM_FIELD_KEYS.productDescription)?.placeholder?.replace("*", "")}
            defaultInvalidMessage={item.labelForDescriptionField}
            value={values[FORM_FIELD_KEYS.productDescription]}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            hideDivider
          />
        </Col>
      </>
    );
  }

  if (item.extraQuestion) {
    const extraValues = Array
      .from({ length: max - start }, (_, i) => i + start + 1)
      .reduce((res, num) => {
        res[`${FORM_FIELD_KEYS.productExtraQuestion}${num}`] = "";
        return res;
      }, { productDescription: "" });

    const handleChange = (e) => onChange && onChange(e, extraValues);
    const extraItem = item.extraQuestionOptions?.find(({ code }) => code === values[`${FORM_FIELD_KEYS.productExtraQuestion}${start}`]);

    return (
      <>
        <Col xs={12}>
          <div className="input-title">
            <span>{item.extraQuestion}</span>
          </div>
        </Col>
        <Col xs={12}>
          <Select
            {...getFieldProps(FORM_FIELD_KEYS.productExtraQuestion)}
            placeholder={getFieldProps(FORM_FIELD_KEYS.productExtraQuestion)?.placeholder?.replace("*", "")}
            id={`${FORM_FIELD_KEYS.productExtraQuestion}${start}`}
            options={createOptions(item.extraQuestionOptions)}
            value={values[`${FORM_FIELD_KEYS.productExtraQuestion}${start}`]}
            validated={values.validated}
            onChange={handleChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            hideDivider={!extraItem?.labelForDescriptionField}
          />
        </Col>
        {renderExtraFields(extraItem, values, onMouseEnter, onMouseLeave, onChange, start + 1)}
      </>
    );
  }

  return null;
}

function ActivityBlock({ values = {}, onChange, onMouseEnter, onMouseLeave }) {
  const [primaryActivities, setPrimaryActivities] = useState([]);
  const showOnlyReasonForApplying = values.code === "EIN_Trust";
  const isReasonForApplyingVisible = values[FORM_FIELD_KEYS.trustType] === "Escrow";

  const specificProductsHint = useMemo(() => {
    let firstPart = "Enter the specific products/services of the business.";
    let secondPart = "If you own a computer repair business you could enter “computer repair services”.";

    if (values.code === "EIN_Church") {
      firstPart = "Enter the specific products/services of the Church.";
    }
    if (values.code === "EIN_NonProfit") {
      firstPart = "Enter the specific products/services of the Non Profit.";
    }

    return `${firstPart} ${secondPart}`;
  }, [values]);

  const primaryActivitiesOptions = useMemo(() => {
    return createOptions(primaryActivities);
  }, [primaryActivities]);

  const currentActivity = useMemo(() => {
    return primaryActivities.find(({ code }) => code === values[FORM_FIELD_KEYS.activityPrimaryActivity]);
  }, [values, primaryActivities]);

  const specificProductsOptions = useMemo(() => {
    return createOptions(currentActivity?.specificProducts);
  }, [currentActivity]);

  const currentSpecificProduct = useMemo(() => {
    return currentActivity?.specificProducts.find(({ code }) => code === values[FORM_FIELD_KEYS.activitySpecificProducts]);
  }, [currentActivity, values]);

  const isActivityDescription = Boolean(currentActivity?.labelForDescriptionField);
  const isActivitySpecificProducts = Boolean(currentActivity?.specificProducts?.length > 0);
  const isActivitySpecificProductsLabel = Boolean(currentActivity?.specificProductsLabel);

  const handlePrimaryActivityChange = useCallback((event) => {
    const extraValues = {
      activitySpecificProducts: "",
      [`${FORM_FIELD_KEYS.productExtraQuestion}1`]: "",
      [`${FORM_FIELD_KEYS.productExtraQuestion}2`]: "",
      [`${FORM_FIELD_KEYS.productExtraQuestion}3`]: "",
      [`${FORM_FIELD_KEYS.productExtraQuestion}4`]: "",
      productDescription: "",
    };
    onChange && onChange(event, extraValues);
  }, [onChange]);

  const handleSpecificProductsChange = useCallback((event) => {
    const extraValues = {
      [`${FORM_FIELD_KEYS.productExtraQuestion}1`]: "",
      [`${FORM_FIELD_KEYS.productExtraQuestion}2`]: "",
      [`${FORM_FIELD_KEYS.productExtraQuestion}3`]: "",
      [`${FORM_FIELD_KEYS.productExtraQuestion}4`]: "",
      productDescription: "",
    };

    onChange(event, extraValues);
  }, [onChange]);

  useEffect(() => {
    async function fetchPrimaryActivities() {
      try {
        const result = await axios.get(process.env.NEXT_PUBLIC_REACT_APP_API + "/category/EIN/products/primary-activity");
        setPrimaryActivities(result.data);
      } catch (e) {
        try {
          let salePage = window.location.href;
          let salePageURLArr = salePage.split("/");
          let salePageURL = (salePageURLArr[0] + "//" + salePageURLArr[2]).replace(/\/+$/, "");
          const result2 = await axios.get(salePageURL + "/crm/category/EIN/products/primary-activity");
          setPrimaryActivities(result2.data);
        } catch (e) {
          Sentry.captureException(new Error("Primary Activity unavailable"), { tags: { section: "products" } });
        }
      }
    }

    fetchPrimaryActivities();
  }, []);

  if (showOnlyReasonForApplying) {
    return (
      <Row>
        <Col xs={12}>
          <div className="inputs-block-title">
            <span>Activity</span>
          </div>
        </Col>
        {isReasonForApplyingVisible && (
          <>
            <Col xs={12}>
              <Select
                {...getFieldProps(FORM_FIELD_KEYS.activityReasonForApplying)}
                placeholder={getFieldProps(FORM_FIELD_KEYS.activityReasonForApplying)?.placeholder?.replace("*", "")}
                value={values[FORM_FIELD_KEYS.activityReasonForApplying]}
                validated={values.validated}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                hideDivider
              />
            </Col>
            <Col xs={12}>
              <div className="inputs-block-divider" />
            </Col>
          </>
        )}
      </Row>
    );
  }

  return (
    <Row>
      <Col xs={12}>
        <div className="inputs-block-title">
          <span>Activity</span>
        </div>
      </Col>
      <Col xs={12}>
        <Select
          {...getFieldProps(FORM_FIELD_KEYS.activityReasonForApplying)}
          placeholder={getFieldProps(FORM_FIELD_KEYS.activityReasonForApplying)?.placeholder?.replace("*", "")}
          value={values[FORM_FIELD_KEYS.activityReasonForApplying]}
          validated={values.validated}
          onChange={onChange}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        />
      </Col>
      <Col xs={12}>
        <Select
          {...getFieldProps(FORM_FIELD_KEYS.activityPrimaryActivity)}
          placeholder={getFieldProps(FORM_FIELD_KEYS.activityPrimaryActivity)?.placeholder?.replace("*", "")}
          options={primaryActivitiesOptions}
          value={values[FORM_FIELD_KEYS.activityPrimaryActivity]}
          validated={values.validated}
          onChange={handlePrimaryActivityChange}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          hideDivider={!isActivityDescription && !isActivitySpecificProducts}
        />
      </Col>
      {isActivityDescription && (
        <>
          <Col xs={12}>
            <div className="input-title">
              <span>{currentActivity.labelForDescriptionField}</span>
            </div>
          </Col>
          <Col xs={12}>
            <TextInput
              {...getFieldProps(FORM_FIELD_KEYS.productDescription)}
              placeholder={getFieldProps(FORM_FIELD_KEYS.productDescription)?.placeholder?.replace("*", "")}
              defaultInvalidMessage={currentActivity.labelForDescriptionField}
              value={values[FORM_FIELD_KEYS.productDescription]}
              onChange={onChange}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              hideDivider={!values[FORM_FIELD_KEYS.productDescription]}
            />
          </Col>
        </>
      )}
      {isActivitySpecificProducts && (
        <>
          {isActivitySpecificProductsLabel && (
            <Col xs={12}>
              <div className="input-title">
                <span>{currentActivity.specificProductsLabel}</span>
              </div>
            </Col>
          )}
          <Col xs={12}>
            <Select
              {...getFieldProps(FORM_FIELD_KEYS.activitySpecificProducts)}
              placeholder={getFieldProps(FORM_FIELD_KEYS.activitySpecificProducts)?.placeholder?.replace("*", "")}
              hint={specificProductsHint}
              options={specificProductsOptions}
              value={values[FORM_FIELD_KEYS.activitySpecificProducts]}
              validated={values.validated}
              onChange={handleSpecificProductsChange}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              hideDivider={!values[FORM_FIELD_KEYS.activitySpecificProducts]}
            />
          </Col>
        </>
      )}
      {renderExtraFields(currentSpecificProduct, values, onMouseEnter, onMouseLeave, onChange)}
      <Col xs={12}>
        <div className="inputs-block-divider" />
      </Col>
    </Row>
  );
}

export default ActivityBlock;
